import React from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import ReactPlaceholder from 'react-placeholder';
import sumPrices from 'utils/price/sumPrices';
import { AppBundle } from 'api/optimalprint-sdk.d';
import getProductId from 'store/product/selector/getProductId';
import getCategoryId from 'store/category/selector/getCategoryId';
import getProductQuantity from 'store/product/selector/getProductQuantity';
import getPrice from 'store/price/selector/getPrice';
import getCurrencyId from 'store/price/selector/getCurrencyId';
import getPriceFormat from 'store/price/selector/getPriceFormat';
import getSelectedBundleProducts from 'store/product/selector/getSelectedBundleProducts';
import { Store } from 'store';
import getFullDate from 'utils/getFullDate';
import getInitialURL from 'store/app/selector/getInitialURL';
import useIsServer from 'hooks/useIsServer';
import PricingBadge from 'components/Common/Prices/PricingBadge/PricingBadge';
import { useTranslation } from 'react-i18next';
import calculateDiscountPercent from 'utils/price/calculateDiscountPercent';
import formatPrice from 'utils/price/formatPrice';
import PriceComponent from 'components/Common/Prices/ProductPrice/PriceComponent';
import isMobile from 'store/app/selector/isMobile';
import OpPlusGetHigherDiscount from 'components/Common/op-ui-wrappers/OpPlusGetHigherDiscount/OpPlusGetHigherDiscount';
import usePlusHigherDiscount from 'hooks/usePlusHigherDiscount';
import styles from './ProductPrice.module.scss';

interface ProductPriceProps {
  theme?: string;
  remark?: string | undefined;
  inOneLine?: boolean;
}
const ProductPrice = ({ theme, remark, inOneLine }: ProductPriceProps) => {
  const productId = useSelector(getProductId);
  const categoryId = useSelector(getCategoryId) || 1;
  const { t } = useTranslation();
  const isDesktop = !useSelector(isMobile);
  const quantity = useSelector(getProductQuantity) || 1;
  const currencyId = useSelector(getCurrencyId);
  const priceFormat = useSelector(getPriceFormat);
  const productPrice = useSelector((store: Store) => getPrice(store, categoryId, productId, quantity));
  const selectedBundledProductsPrice = useSelector((store: Store) => {
    const selectedBundledProducts = getSelectedBundleProducts(store)
      .filter((selectedProduct) => selectedProduct.productId > 0)
      .map((selectedProduct) => getPrice(store, selectedProduct.categoryId, selectedProduct.productId, quantity));

    return selectedBundledProducts as (AppBundle.Api.Entity.Product.V1.IProductPrice | undefined)[];
  });

  const price = sumPrices(productPrice, ...selectedBundledProductsPrice);
  const isDataReady = Boolean(price && priceFormat && currencyId);
  const hasDiscount = price && price.pi !== price.p;

  const currentURL = useIsServer() ? useSelector(getInitialURL) : window.location.href;

  const { isPlusDiscountActive } = usePlusHigherDiscount();

  if (!price) {
    return null;
  }

  const discountedPercent = calculateDiscountPercent(price.p, price.pi);

  const regularPrice = currencyId && formatPrice(price.pi, priceFormat || '', currencyId);
  const discountedPrice = currencyId && formatPrice(price.p, priceFormat || '', currencyId);

  return (
    <div
      className={cn(styles.productPrice, styles[`theme-${theme}`], { [styles.inOneLine]: inOneLine && isDesktop })}
      itemProp="offers"
      itemType="http://schema.org/Offer"
      itemScope
    >
      <ReactPlaceholder
        showLoadingAnimation
        type="textRow"
        ready={isDataReady}
        style={{ width: '100px', backgroundColor: '#e8e8e8' }}
      >
        <meta itemProp="price" content={String(price.p)} />
        <meta itemProp="priceCurrency" content={currencyId} />
        <meta itemProp="priceValidUntil" content={getFullDate(new Date())} />
        <meta itemProp="url" content={currentURL} />
        <meta itemProp="itemCondition" content="https://schema.org/NewCondition" />
        <meta itemProp="availability" content="https://schema.org/InStock" />
        {isDesktop && (
          <div className={styles.discountRow}>
            {!isPlusDiscountActive && hasDiscount && <PricingBadge discount={discountedPercent} />}
            <OpPlusGetHigherDiscount
              product-id={productId}
              discount={Number(discountedPercent)}
              alternative={hasDiscount}
            />
          </div>
        )}
        {!isDesktop && (
          <div className={styles.discountRow}>
            {!isPlusDiscountActive && Boolean(hasDiscount && !isDesktop) && (
              <div className={styles.mobileDiscountBadge}>
                <PricingBadge discount={discountedPercent} />
              </div>
            )}
            <OpPlusGetHigherDiscount
              product-id={productId}
              discount={Number(discountedPercent)}
              alternative={hasDiscount}
            />
          </div>
        )}
        <div className={cn(styles.priceContainer, { [styles.withDiscount]: hasDiscount })}>
          <PriceComponent
            hasDiscount={hasDiscount}
            initialPrice={price.pi}
            initialPriceFormatted={regularPrice}
            finalPrice={price.p}
            finalPriceFormatted={discountedPrice}
            remark={remark}
            theme={theme}
            discountedPriceFirst
            isPlusDiscountActive={isPlusDiscountActive}
          />
          <span className={styles.vatPrice}>{t('txt_incl_vat')}</span>
        </div>
      </ReactPlaceholder>
    </div>
  );
};

export default ProductPrice;
